import type {
  FreezeDeliveryPath,
  FreezeEventWire,
} from '../../../shared/diagnostics/FreezeEventTypes';

export const PRE_ROOM_FREEZE_QUEUE_CAPACITY = 16;

export interface FreezeEventTransport {
  send(type: 'netobs:freezeEvent', payload: FreezeEventWire): void;
}

interface PendingFreezeEvent {
  wire: FreezeEventWire;
  bufferedAt: number;
  replayPath: Extract<FreezeDeliveryPath, 'pre_room_replay' | 'send_retry'>;
}

export interface FreezeDeliverySnapshot {
  capacity: number;
  pending: number;
  oldestCapturedAt: number | null;
  liveSent: number;
  preRoomBuffered: number;
  sendRetryBuffered: number;
  replayed: number;
  dropped: number;
  sendFailures: number;
  duplicatesSuppressed: number;
}

export type FreezeSubmitResult = 'live-sent' | 'buffered' | 'duplicate';

/**
 * Page-lifetime delivery owner for FreezeSentinel events.
 *
 * The queue exists only to bridge the short boot window before NetworkManager
 * exposes a room. DomainLog remains the durable server store; this class adds
 * no timer, endpoint, or second analysis bus. Successful sends are removed
 * synchronously, so repeated room-bind checks cannot replay them twice.
 */
export class FreezeEventDeliveryQueue {
  private readonly pending: PendingFreezeEvent[] = [];
  private readonly pendingIds = new Set<string>();
  private readonly deliveredIds: string[] = [];
  private readonly deliveredIdSet = new Set<string>();
  private liveSent = 0;
  private preRoomBuffered = 0;
  private sendRetryBuffered = 0;
  private replayed = 0;
  private dropped = 0;
  private sendFailures = 0;
  private duplicatesSuppressed = 0;

  constructor(
    private readonly capacity = PRE_ROOM_FREEZE_QUEUE_CAPACITY,
    private readonly wallNow: () => number = Date.now,
  ) {
    if (!Number.isInteger(capacity) || capacity < 1) {
      throw new Error('FreezeEventDeliveryQueue capacity must be a positive integer');
    }
  }

  submit(wire: FreezeEventWire, transport: FreezeEventTransport | null): FreezeSubmitResult {
    if (this.isDuplicate(wire.clientEventId)) {
      this.duplicatesSuppressed += 1;
      return 'duplicate';
    }

    if (!transport) {
      this.enqueue(wire, 'pre_room_replay');
      return 'buffered';
    }

    try {
      this.send(transport, wire, 'live', null);
      this.liveSent += 1;
      this.rememberDelivered(wire.clientEventId);
      return 'live-sent';
    } catch {
      this.sendFailures += 1;
      this.enqueue(wire, 'send_retry');
      return 'buffered';
    }
  }

  flush(transport: FreezeEventTransport | null): number {
    if (!transport || this.pending.length === 0) return 0;

    let sent = 0;
    while (this.pending.length > 0) {
      const next = this.pending[0];
      try {
        this.send(transport, next.wire, next.replayPath, next.bufferedAt);
      } catch {
        this.sendFailures += 1;
        break;
      }
      this.pending.shift();
      if (next.wire.clientEventId) this.pendingIds.delete(next.wire.clientEventId);
      this.rememberDelivered(next.wire.clientEventId);
      this.replayed += 1;
      sent += 1;
    }
    return sent;
  }

  getSnapshot(): FreezeDeliverySnapshot {
    return {
      capacity: this.capacity,
      pending: this.pending.length,
      oldestCapturedAt: this.pending[0]?.wire.capturedAt ?? null,
      liveSent: this.liveSent,
      preRoomBuffered: this.preRoomBuffered,
      sendRetryBuffered: this.sendRetryBuffered,
      replayed: this.replayed,
      dropped: this.dropped,
      sendFailures: this.sendFailures,
      duplicatesSuppressed: this.duplicatesSuppressed,
    };
  }

  private enqueue(
    wire: FreezeEventWire,
    replayPath: PendingFreezeEvent['replayPath'],
  ): void {
    if (this.pending.length >= this.capacity) {
      const evicted = this.pending.shift();
      if (evicted?.wire.clientEventId) this.pendingIds.delete(evicted.wire.clientEventId);
      this.dropped += 1;
    }
    this.pending.push({ wire, bufferedAt: this.wallNow(), replayPath });
    if (wire.clientEventId) this.pendingIds.add(wire.clientEventId);
    if (replayPath === 'pre_room_replay') this.preRoomBuffered += 1;
    else this.sendRetryBuffered += 1;
  }

  private send(
    transport: FreezeEventTransport,
    wire: FreezeEventWire,
    path: FreezeDeliveryPath,
    bufferedAt: number | null,
  ): void {
    const sentAt = this.wallNow();
    transport.send('netobs:freezeEvent', {
      ...wire,
      delivery: { path, bufferedAt, sentAt },
    });
  }

  private isDuplicate(clientEventId: string | undefined): boolean {
    return Boolean(clientEventId
      && (this.pendingIds.has(clientEventId) || this.deliveredIdSet.has(clientEventId)));
  }

  private rememberDelivered(clientEventId: string | undefined): void {
    if (!clientEventId || this.deliveredIdSet.has(clientEventId)) return;
    this.deliveredIds.push(clientEventId);
    this.deliveredIdSet.add(clientEventId);
    const deliveredCapacity = this.capacity * 4;
    if (this.deliveredIds.length > deliveredCapacity) {
      const evicted = this.deliveredIds.shift();
      if (evicted) this.deliveredIdSet.delete(evicted);
    }
  }
}
